import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, Field } from "@/components/ranger/PageHeader";
import { BTN_PRIMARY } from "@/pages/Servers";
import { api, errMsg, fmtTime } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const inputCls = "border-[#1E2D4A] bg-[#050811] font-mono";

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center justify-between gap-3 text-sm text-slate-300">
    {label}<Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
  </label>
);

const RecentWarnings = ({ rows }) => (
  <ul data-testid="purge-recent-warnings" className="mt-5 space-y-1 border-t border-[#1E2D4A] pt-4 font-mono text-xs">
    <li className="rg-label mb-1">Recent warnings sent</li>
    {rows.length === 0 && <li className="text-slate-500">None yet.</li>}
    {rows.map((w) => <li key={`${w.offset}-${w.created_at}`} className="text-slate-400">{w.offset === -1 ? "Wipe (Discord)" : w.offset === -2 ? "Wipe (in-game)" : `T-${w.offset}m`} · {fmtTime(w.created_at)} · discord: {w.result?.discord ?? "—"} · in-game: {w.result?.ingame ?? "—"}</li>)}
  </ul>
);

export const PurgeSettings = ({ cid }) => {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get(`/communities/${cid}/purge`).then((r) => { setData(r.data); setCfg(r.data.config); }); }, [cid]);
  if (!cfg) return null;
  const set = (k) => (v) => setCfg({ ...cfg, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    try {
      const warnings = String(cfg.warnings_text ?? cfg.warnings_minutes.join(", ")).split(",").map((x) => parseInt(x, 10)).filter((x) => !Number.isNaN(x));
      const { warnings_text, ...rest } = cfg;
      const { data: d } = await api.put(`/communities/${cid}/purge`, { ...rest, lead_hours: Number(cfg.lead_hours), warnings_minutes: warnings, time_confirmed: true });
      setData(d); setCfg(d.config); toast.success("Purge schedule saved");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card title="Purge schedule" icon={Flame} testId="purge-settings" className="lg:col-span-2">
      <form onSubmit={save} className="grid gap-5 md:grid-cols-2">
        <div className="space-y-4">
          <Toggle label="Purge scheduling enabled" checked={cfg.enabled} onChange={set("enabled")} testId="purge-enabled-switch" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Wipe day (last … of month)">
              <Select value={String(cfg.wipe_weekday)} onValueChange={(v) => set("wipe_weekday")(Number(v))}>
                <SelectTrigger data-testid="purge-weekday-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="border-[#1E2D4A] bg-[#0B1120]">{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Wipe time" hint={cfg.time_confirmed ? null : "Placeholder — confirm"}><Input data-testid="purge-wipe-time-input" type="time" required value={cfg.wipe_time} onChange={(e) => set("wipe_time")(e.target.value)} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Timezone"><Input data-testid="purge-timezone-input" value={cfg.timezone} onChange={(e) => set("timezone")(e.target.value)} className={inputCls} /></Field>
            <Field label="Purge starts (hours before wipe)"><Input data-testid="purge-lead-hours-input" type="number" min={1} max={336} value={cfg.lead_hours} onChange={(e) => set("lead_hours")(e.target.value)} className={inputCls} /></Field>
          </div>
        </div>
        <div className="space-y-4">
          <Field label="Warnings (minutes before start)" hint="0 = when the purge begins. 1440 = 24 hours."><Input data-testid="purge-warnings-input" value={cfg.warnings_text ?? cfg.warnings_minutes.join(", ")} onChange={(e) => set("warnings_text")(e.target.value)} className={inputCls} /></Field>
          <Toggle label="Post warnings to Discord alert channel" checked={cfg.discord} onChange={set("discord")} testId="purge-discord-switch" />
          <Toggle label="Broadcast warnings in-game (RCON say)" checked={cfg.ingame} onChange={set("ingame")} testId="purge-ingame-switch" />
          <Toggle label="Announce fresh wipe after each wipe" checked={cfg.wipe_announce} onChange={set("wipe_announce")} testId="wipe-announce-switch" />
          <div className="grid grid-cols-[1fr_2fr] gap-3">
            <Field label="Minutes after wipe"><Input data-testid="wipe-delay-input" type="number" min={0} max={120} value={cfg.wipe_announce_delay_minutes} onChange={(e) => set("wipe_announce_delay_minutes")(Number(e.target.value))} className={inputCls} /></Field>
            <Field label="Wipe message" hint="No quotes or semicolons. Sent in-game once RCON reconnects."><Input data-testid="wipe-message-input" maxLength={180} value={cfg.wipe_message} onChange={(e) => set("wipe_message")(e.target.value)} className="border-[#1E2D4A] bg-[#050811]" /></Field>
          </div>
          <button data-testid="purge-save-button" className={BTN_PRIMARY}><Save className="h-4 w-4" />Save schedule</button>
        </div>
      </form>
      <RecentWarnings rows={data.recent_warnings} />
    </Card>
  );
};
